import React, { useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { SystemSettings } from "@/components/admin/SystemSettings";
// import { RoleManagement } from "@/components/admin/RoleManagement";
import UsersExplorer from "../components/admin/users/UsersExplorer";
import { ClassificationManagement } from "@/components/admin/classification/ClassificationManagement";
import { CsvUtilsContent } from "@/components/admin/csv-utils/CsvUtilsContent";
import { ProviderStatus } from "@/components/admin/ProviderStatus";

import { useUserRole } from "@/hooks/useUserRole";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import SettingsTabsHeader from "@/components/settings/SettingsTabsHeader";
import { PageContainer } from "@/components/ui/PageContainer";

export function AdminPage() {
  const { user } = useAuthStore();
  const { userRole, isLoading: roleLoading } = useUserRole();
  const [activeTab, setActiveTab] = useState<"users" | "system" | "classification" | "data" | "monitor">("users");

  if (!user) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Please sign in to access admin settings
        </AlertDescription>
      </Alert>
    );
  }

  if (roleLoading) {
    return <p>Loading...</p>;
  }

  if (userRole !== 'sys_admin') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You do not have permission to view this page.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 sticky top-0 bg-background z-10">
        <SidebarTrigger className="-ml-2" />
        <Separator orientation="vertical" className="h-6" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link to="/">Dashboard</Link>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Admin</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>
      <PageContainer maxWidthClassName="max-w-[960px]">
        <div className="flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Admin</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage system settings and configurations
              </p>
            </div>
          </div>

          <div className="mt-6">
            <SettingsTabsHeader 
              tabs={[
                { id: "users" as const, label: "Users" },
                { id: "system" as const, label: "System" },
                { id: "classification" as const, label: "Classification" },
                { id: "data" as const, label: "Data" },
                { id: "monitor" as const, label: "Monitor" },
              ]}
              activeTab={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId)}
            />
          </div>

          <div className="mt-4">
            {activeTab === "users" && <UsersExplorer />}
            {activeTab === "system" && (
              <div className="space-y-4">
                <SystemSettings />
              </div>
            )}
            {activeTab === "classification" && <ClassificationManagement />}
            {activeTab === "data" && <CsvUtilsContent />}
            {activeTab === "monitor" && <ProviderStatus />}
          </div>
        </div>
      </PageContainer>
    </>
  );
}
